import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useStatsigClient as useBoundStatsigClient } from "@statsig/react-bindings";
import { isRouteErrorResponse, useLocation, useMatches, useNavigationType } from "react-router";
import {
  AdsManagerNavigationType,
  AdsManagerRouteLoad,
} from "@oai/analytics/gen/client-events/protobuf_analytics_events/v1/tapestry";

import { useGlobalContext } from "@/hooks/useGlobalContext";
import {
  initializeAnalytics,
  logStructuredEvent as logStructuredAnalyticsEvent,
} from "@/lib/analytics";
import { SofaClientRequestError } from "@/lib/sofaClient";
import { SofaApiError } from "@/lib/sofaErrors";
import { statsc } from "@/lib/statsc";

import type { ReactNode } from "react";
import type { StatsigClient } from "@statsig/react-bindings";
import type { MessageFns, MessagePayload, NoExtraKeys } from "@oai/analytics/analytics-logger";
import type { StatscTags } from "@/lib/statsc";

type ClientTelemetryValue = string | number | null | undefined;
export type ClientTelemetryMetadataValue =
  | string
  | number
  | boolean
  | readonly string[]
  | null
  | undefined;
export type ClientTelemetryMetadata = Record<string, ClientTelemetryMetadataValue>;
export type StructuredClientErrorMetadata = {
  errorType: string;
  errorCode?: string;
  httpStatus?: number;
  errorMessage?: string;
};

const STRUCTURED_ERROR_MESSAGE_MAX_LENGTH = 200;
const TIME_TO_FIRST_PAINT_EVENT_NAME = "bazaar.tapestry.time_to_first_paint";
const MAX_ATTRIBUTION_VALUE_LENGTH = 256;
export const TAPESTRY_CLIENT_STATSC_NAMESPACE = "bazaar.tapestry";
export const TAPESTRY_CLIENT_ERROR_BOUNDARY_STATSC_METRIC = "client_error_boundary";
const UNKNOWN_STATSC_TAG = "unknown";
const NONE_STATSC_TAG = "none";
const STATSC_TAG_MAX_LENGTH = 120;
const DYNAMIC_PATH_ID_PREFIX_PATTERN =
  /^(?:act|acct|ad|adacct|adgroup|adgrp|api_key|audience|bulk_update_job|campaign|campaign_clone_job|camp|client_data_source|cmp|cmpn|conversion_event_setting|feed|file|import_job|job|oppref|prefill|product_feed|req|request|spend_limit_window|user|usr)_[a-z0-9][a-z0-9_-]*$/i;
const OPENAPI_ID_PATH_PARAM_PATTERN = /^\{[a-z0-9_]*id\}$/i;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ClientStructuredTelemetryPayload<M extends MessageFns<any, string>> = Omit<
  MessagePayload<M>,
  "adAccountId"
>;

type ClientTelemetry = {
  logEvent: (
    eventName: string,
    value?: ClientTelemetryValue,
    metadata?: ClientTelemetryMetadata,
  ) => void;
  logStructuredEvent: <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    M extends MessageFns<any, string>,
    Payload extends ClientStructuredTelemetryPayload<M>,
  >(
    messageType: M,
    payload: NoExtraKeys<Payload, ClientStructuredTelemetryPayload<M>>,
  ) => string | undefined;
};

const NOOP_CLIENT_TELEMETRY: ClientTelemetry = {
  logEvent: () => {},
  logStructuredEvent: () => undefined,
};

export const ClientTelemetryContext = createContext<ClientTelemetry>(NOOP_CLIENT_TELEMETRY);

export function ClientTelemetryProvider({ children }: { children: ReactNode }) {
  const { client } = useBoundStatsigClient();
  const clientRef = useRef(client);
  const { adAccountId, user } = useGlobalContext();

  useEffect(() => {
    void initializeAnalytics(user, client as StatsigClient);
  }, [client, user]);

  const logEvent = useMemo<ClientTelemetry["logEvent"]>(
    () => (eventName, value, metadata) => {
      try {
        clientRef.current.logEvent(
          getClientTelemetryEventName(eventName),
          value ?? undefined,
          sanitizeMetadata(metadata),
        );
      } catch (error) {
        console.warn("Failed to log Statsig client event", {
          eventName,
          error,
        });
      }
    },
    [],
  );
  const logStructuredEvent = useMemo<ClientTelemetry["logStructuredEvent"]>(
    () => (messageType, payload) => {
      try {
        return logStructuredAnalyticsEvent(messageType, {
          ...payload,
          adAccountId,
        } as NoExtraKeys<MessagePayload<typeof messageType>, MessagePayload<typeof messageType>>);
      } catch (error) {
        console.warn("Failed to log structured client telemetry event", {
          messageType: messageType.$type,
          payload,
          error,
        });
        return undefined;
      }
    },
    [adAccountId],
  );
  const telemetry = useMemo<ClientTelemetry>(
    () => ({ logEvent, logStructuredEvent }),
    [logEvent, logStructuredEvent],
  );

  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  return (
    <ClientTelemetryContext.Provider value={telemetry}>{children}</ClientTelemetryContext.Provider>
  );
}

export function useClientTelemetry(): ClientTelemetry {
  return useContext(ClientTelemetryContext);
}

function readBoundedSearchParam(searchParams: URLSearchParams, name: string): string | undefined {
  const values = searchParams.getAll(name);
  if (values.length !== 1) {
    return undefined;
  }

  const value = values[0]?.trim();
  if (
    !value ||
    value.length > MAX_ATTRIBUTION_VALUE_LENGTH ||
    Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 0x1f || codePoint === 0x7f;
    })
  ) {
    return undefined;
  }

  return value;
}

export function getUtmParameters(search: string) {
  const searchParams = new URLSearchParams(search);

  return {
    utmSource: readBoundedSearchParam(searchParams, "utm_source"),
    utmMedium: readBoundedSearchParam(searchParams, "utm_medium"),
    utmCampaign: readBoundedSearchParam(searchParams, "utm_campaign"),
    utmContent: readBoundedSearchParam(searchParams, "utm_content"),
    utmTerm: readBoundedSearchParam(searchParams, "utm_term"),
  };
}

export function ClientFirstPaintLogger() {
  const { logEvent } = useClientTelemetry();
  const location = useLocation();
  const initialRouteRef = useRef(location.pathname);
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    let isCancelled = false;
    let observer: PerformanceObserver | undefined;

    const logFirstPaint = (timeToFirstPaintMs: number) => {
      if (isCancelled || hasLoggedRef.current) {
        return;
      }

      hasLoggedRef.current = true;
      observer?.disconnect();

      logEvent(TIME_TO_FIRST_PAINT_EVENT_NAME, timeToFirstPaintMs, {
        route: initialRouteRef.current,
      });
    };

    const firstPaintTime = getFirstPaintTime();
    if (firstPaintTime != null) {
      logFirstPaint(firstPaintTime);
      return () => {
        isCancelled = true;
      };
    }

    if (typeof PerformanceObserver !== "undefined") {
      try {
        observer = new PerformanceObserver((list) => {
          const observedFirstPaintTime = getPerformanceEntryStartTime(
            list.getEntriesByName("first-paint").at(0),
          );
          if (observedFirstPaintTime != null) {
            logFirstPaint(observedFirstPaintTime);
          }
        });
        observer.observe({ type: "paint", buffered: true });
      } catch {
        // Paint timing is unavailable; skip this metric rather than logging post-hydration RAF timing.
      }
    }

    return () => {
      isCancelled = true;
      observer?.disconnect();
    };
  }, [logEvent]);

  return null;
}

export function ClientRouteLoadLogger() {
  const { logEvent, logStructuredEvent } = useClientTelemetry();
  const location = useLocation();
  const matches = useMatches();
  const navigationType = useNavigationType();
  const isFirstLoadRef = useRef(true);
  const leafRouteId = matches.at(-1)?.id ?? "unknown";
  const routeStack = matches.map((match) => match.id).join(" > ") || "unknown";

  useEffect(() => {
    const hasQuery = location.search.length > 0;
    const hasHash = location.hash.length > 0;
    const isInitialLoad = isFirstLoadRef.current;
    const routeIds = routeStack === "unknown" ? [] : routeStack.split(" > ");
    const utmParameters = getUtmParameters(location.search);

    const eventId = logStructuredEvent(AdsManagerRouteLoad, {
      route: location.pathname,
      leafRouteId,
      routeIds,
      navigationType: getAnalyticsNavigationType(navigationType),
      hasQuery,
      hasHash,
      isInitialLoad,
      ...utmParameters,
    });
    const legacyMetadata = {
      route: location.pathname,
      leaf_route_id: leafRouteId,
      route_stack: routeStack,
      navigation_type: navigationType,
      has_query: hasQuery,
      has_hash: hasHash,
      is_initial_load: isInitialLoad,
      utm_source: utmParameters.utmSource,
      utm_medium: utmParameters.utmMedium,
      utm_campaign: utmParameters.utmCampaign,
      utm_content: utmParameters.utmContent,
      utm_term: utmParameters.utmTerm,
      ...(eventId ? { unique_event_id: eventId } : {}),
    };
    logEvent("route_load", undefined, legacyMetadata);
    isFirstLoadRef.current = false;
  }, [
    leafRouteId,
    location.hash,
    location.pathname,
    location.search,
    logEvent,
    logStructuredEvent,
    navigationType,
    routeStack,
  ]);

  return null;
}

export function useClientErrorBoundaryTelemetry(
  error: unknown,
  options: {
    boundary: string;
    surface?: string;
  },
) {
  const { logEvent } = useClientTelemetry();
  const location = useLocation();
  const { boundary, surface } = options;

  useEffect(() => {
    const metadata = getClientErrorBoundaryTelemetryMetadata(error, {
      boundary,
      surface,
      route: location.pathname,
    });
    logEvent("error_boundary", undefined, metadata);
    recordClientErrorBoundaryStatsc(metadata);
  }, [boundary, error, location.pathname, logEvent, surface]);
}

export function getClientErrorBoundaryTelemetryMetadata(
  error: unknown,
  context: {
    boundary: string;
    route: string;
    surface?: string;
  },
): ClientTelemetryMetadata {
  return {
    boundary: context.boundary,
    surface: context.surface,
    route: context.route,
    ...getClientErrorTelemetryMetadata(error),
  };
}

export function getClientErrorTelemetryMetadata(error: unknown): ClientTelemetryMetadata {
  if (isRouteErrorResponse(error)) {
    return {
      error_type: "route_error_response",
      status_code: error.status,
      error_message: error.statusText,
    };
  }

  if (error instanceof SofaClientRequestError) {
    return {
      error_type: error.name,
      error_message: error.message,
      request_method: error.method,
      request_path: error.path,
      response_content_type: error.responseContentType,
      sofa_error_reason: error.reason,
      status_code: error.status,
      request_id: error.requestId,
    };
  }

  if (error instanceof SofaApiError) {
    return {
      error_type: error.name || "SofaApiError",
      error_code: error.code,
      status_code: error.status,
      error_message: error.message,
    };
  }

  if (error instanceof Error) {
    return {
      error_type: error.name || "Error",
      error_message: error.message,
    };
  }

  return {
    error_type: typeof error,
    error_message: String(error),
  };
}

export function recordClientErrorBoundaryStatsc(metadata: ClientTelemetryMetadata): void {
  try {
    statsc.count(
      TAPESTRY_CLIENT_STATSC_NAMESPACE,
      TAPESTRY_CLIENT_ERROR_BOUNDARY_STATSC_METRIC,
      getClientErrorBoundaryStatscTags(metadata),
    );
  } catch {
    // Client operational metrics are best-effort and must not affect error rendering.
  }
}

export function getClientErrorBoundaryStatscTags(metadata: ClientTelemetryMetadata): StatscTags {
  return {
    boundary: getStatscStringTag(metadata.boundary, UNKNOWN_STATSC_TAG),
    surface: getStatscStringTag(metadata.surface, NONE_STATSC_TAG),
    route: getStatscPathTag(metadata.route, UNKNOWN_STATSC_TAG),
    error_type: getStatscStringTag(metadata.error_type, UNKNOWN_STATSC_TAG),
    status_class: getStatscStatusClassTag(metadata.status_code),
    request_method: getStatscStringTag(metadata.request_method, NONE_STATSC_TAG),
    request_path: getStatscPathTag(metadata.request_path, NONE_STATSC_TAG),
    sofa_error_reason: getStatscStringTag(metadata.sofa_error_reason, NONE_STATSC_TAG),
  };
}

export function getStructuredClientErrorMetadata(error: unknown): StructuredClientErrorMetadata {
  const metadata = getClientErrorTelemetryMetadata(error);
  const errorType =
    typeof metadata.error_type === "string" && metadata.error_type.length > 0 ?
      metadata.error_type
    : "unknown";

  return {
    errorType,
    errorCode: typeof metadata.error_code === "string" ? metadata.error_code : undefined,
    httpStatus: typeof metadata.status_code === "number" ? metadata.status_code : undefined,
    errorMessage: sanitizeStructuredErrorMessage(metadata.error_message),
  };
}

function sanitizeStructuredErrorMessage(value: ClientTelemetryMetadataValue): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length === 0) {
    return undefined;
  }

  return normalized.slice(0, STRUCTURED_ERROR_MESSAGE_MAX_LENGTH);
}

function getStatscStringTag(value: ClientTelemetryMetadataValue, fallback: string): string {
  if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
    return fallback;
  }

  const normalized = String(value).replace(/\s+/g, "_").trim();
  if (normalized.length === 0) {
    return fallback;
  }

  return normalized.slice(0, STATSC_TAG_MAX_LENGTH);
}

function getStatscPathTag(value: ClientTelemetryMetadataValue, fallback: string): string {
  const path = getStatscStringTag(value, fallback);
  if (path === fallback) {
    return path;
  }

  const pathWithoutQuery = path.split("?")[0]?.split("#")[0] ?? "";
  if (pathWithoutQuery.length === 0) {
    return fallback;
  }

  return pathWithoutQuery
    .split("/")
    .map((segment) => (isDynamicPathSegment(segment) ? ":id" : segment))
    .join("/")
    .slice(0, STATSC_TAG_MAX_LENGTH);
}

function isDynamicPathSegment(segment: string): boolean {
  if (segment.length === 0) {
    return false;
  }

  if (OPENAPI_ID_PATH_PARAM_PATTERN.test(segment) || DYNAMIC_PATH_ID_PREFIX_PATTERN.test(segment)) {
    return true;
  }

  if (/^\d+$/.test(segment)) {
    return true;
  }

  if (/^[0-9a-f-]{8,}$/i.test(segment)) {
    return true;
  }

  return segment.length >= 8 && /\d/.test(segment);
}

function getStatscStatusClassTag(value: ClientTelemetryMetadataValue): string {
  const status =
    typeof value === "number" ? value
    : typeof value === "string" ? Number(value)
    : undefined;

  if (status == null || !Number.isFinite(status) || status < 100 || status > 599) {
    return NONE_STATSC_TAG;
  }

  return `${Math.floor(status / 100)}xx`;
}

function getFirstPaintTime(): number | undefined {
  if (typeof performance === "undefined" || typeof performance.getEntriesByName !== "function") {
    return undefined;
  }

  return getPerformanceEntryStartTime(performance.getEntriesByName("first-paint").at(0));
}

function getPerformanceEntryStartTime(entry: PerformanceEntry | undefined): number | undefined {
  const startTime = entry?.startTime;
  if (typeof startTime === "number" && Number.isFinite(startTime) && startTime >= 0) {
    return startTime;
  }

  return undefined;
}

function getAnalyticsNavigationType(navigationType: string): AdsManagerNavigationType {
  switch (navigationType) {
    case "POP":
      return AdsManagerNavigationType.ADS_MANAGER_NAVIGATION_TYPE_POP;
    case "PUSH":
      return AdsManagerNavigationType.ADS_MANAGER_NAVIGATION_TYPE_PUSH;
    case "REPLACE":
      return AdsManagerNavigationType.ADS_MANAGER_NAVIGATION_TYPE_REPLACE;
    default:
      return AdsManagerNavigationType.ADS_MANAGER_NAVIGATION_TYPE_UNSPECIFIED;
  }
}

function getClientTelemetryEventName(eventName: string): string {
  return eventName.startsWith("bazaar.tapestry.") ? eventName : `bazaar.tapestry.${eventName}`;
}

function sanitizeMetadata(metadata?: ClientTelemetryMetadata): Record<string, string> {
  if (!metadata) {
    return {};
  }

  const sanitizedEntries = Object.entries(metadata).flatMap(([key, value]) => {
    if (value == null) {
      return [];
    }

    return [[key, Array.isArray(value) ? JSON.stringify(value) : String(value)] as const];
  });

  return Object.fromEntries(sanitizedEntries);
}
